/**
 * Email verification page
 */

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, ShieldCheck, RefreshCcw } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useVerifyEmail, useResendVerification } from '../../hooks/useAuth';
import { usePendingVerificationEmail, useAppStore } from '../../stores/appStore';
import { Link } from 'react-router-dom';

const verifySchema = z.object({
  email: z.string().email('Email inválido'),
  code: z.string().min(6, 'Código de 6 dígitos').max(6, 'Código de 6 dígitos'),
});

type VerifyForm = z.infer<typeof verifySchema>;

export default function VerifyEmailPage() {
  const pendingEmail = usePendingVerificationEmail();
  const { setPendingVerificationEmail } = useAppStore();
  const verifyMutation = useVerifyEmail();
  const resendMutation = useResendVerification();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<VerifyForm>({
    resolver: zodResolver(verifySchema),
    defaultValues: {
      email: pendingEmail || '',
      code: '',
    },
  });

  const [cooldown, setCooldown] = useState<number>(0);

  useEffect(() => {
    if (pendingEmail) {
      setValue('email', pendingEmail);
    }
  }, [pendingEmail, setValue]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => Math.max(c - 1, 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const onSubmit = (data: VerifyForm) => {
    verifyMutation.mutate({
      email: data.email,
      code: data.code,
    });
  };

  const handleResend = () => {
    const email = (getValues('email') || pendingEmail || '').trim();
    if (!email || cooldown > 0) return;
    setPendingVerificationEmail(email);
    resendMutation.mutate(email, {
      onSuccess: () => setCooldown(60),
      onError: () => setCooldown(60),
    });
  };

  const resendLabel = useMemo(
    () => (cooldown > 0 ? `Reenviar código (${cooldown}s)` : 'Reenviar código'),
    [cooldown]
  );

  return (
    <div>
      <div className="text-center mb-8 space-y-2">
        <div className="mx-auto w-12 h-12 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center dark:bg-primary-950 dark:text-primary-300">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Verifique seu email</h1>
        <p className="text-neutral-600 dark:text-neutral-400">
          Enviamos um código de 6 dígitos para {pendingEmail ? <strong className="text-neutral-900 dark:text-white">{pendingEmail}</strong> : 'seu email'}. Digite-o abaixo para ativar sua conta.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <Input
          id="email"
          {...register('email')}
          type="email"
          placeholder="Seu email"
          icon={<Mail className="w-5 h-5" />}
          error={errors.email?.message}
          autoComplete="email"
          onBlur={(e) => setPendingVerificationEmail(e.target.value)}
        />

        <Input
          {...register('code')}
          type="text"
          inputMode="numeric"
          placeholder="Código de 6 dígitos"
          icon={<ShieldCheck className="w-5 h-5" />}
          error={errors.code?.message}
          maxLength={6}
          autoComplete="one-time-code"
        />

        <Button
          type="submit"
          className="w-full"
          loading={verifyMutation.isPending}
        >
          Confirmar email
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleResend}
          loading={resendMutation.isPending}
          disabled={cooldown > 0}
          icon={<RefreshCcw className="w-4 h-4" />}
        >
          {resendLabel}
        </Button>
      </form>

      <div className="mt-8 space-y-2 text-sm text-neutral-500 dark:text-neutral-400">
        <p>Não recebeu? Verifique a pasta de spam ou aguarde 1 minuto antes de tentar reenviar.</p>
        <p>Digitou o email errado? <Link to="/cadastro" className="text-primary-600 dark:text-primary-400">Crie novamente</Link> ou <Link to="/entrar" className="text-primary-600 dark:text-primary-400">volte ao login</Link>.</p>
      </div>
    </div>
  );
}
